import React, { useEffect, useState } from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useBoostAnimations } from '../../../hooks/useBoostAnimations.js';
import { BOOST_ANIMATION_SLOT, boostAnimationError } from '../../../_helpers/boostAnimations.js';
import { FxPreview } from './FxTryOnPanel.jsx';
import '../../../styles/FxTryOnPanel.css';
import '../../../styles/BoostAnimationPicker.css';

/** A boost animation as the item FxPreview plays. */
const previewItem = (item) => ({
  skin: { id: item.id, name: item.name, type: BOOST_ANIMATION_SLOT, fxPackageId: item.fxPackageId },
});

/**
 * The boost animation in the Boost desk (ENG-814, FX-7): the one this
 * listener wears, which everyone sees when they boost, a live preview of it
 * on a sample album card, and their other owned ones to pick from.
 *
 * Picking a chip previews it; "Use it" equips it
 * (`PUT /me/skins/equipped/boost_animation`), and from the 2xx on it is the
 * worn one: the server plays it on the next boost. The preview is FxPreview,
 * on a stage of its own (never the queue's), and follows Reduce effects
 * (ENG-810). It plays only when asked for, so opening the desk starts no
 * second stage.
 *
 * Signed in only: the list is the listener's skin shelf.
 */
const BoostAnimationPicker = ({ previewTiming = null }) => {
  const { t } = useLocale();
  const anims = useBoostAnimations({ enabled: true });
  const { status, items, equipped, equippedId, busyId, error } = anims;
  const [selectedId, setSelectedId] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saved, setSaved] = useState(null);

  // The selection follows the worn one until the listener picks another.
  useEffect(() => {
    if (!selectedId && equippedId) setSelectedId(equippedId);
  }, [selectedId, equippedId]);

  const selected = items.find((item) => item.id === selectedId) ?? equipped;
  const busy = Boolean(busyId);

  const choose = (item) => {
    setSelectedId(item.id);
    setSaved(null);
    setPreviewing(true);
  };
  const use = () => {
    if (!selected) return;
    setSaved(null);
    anims.pick(selected).then((ok) => { if (ok) setSaved(selected.name); });
  };

  let message = null;
  if (status === 'loading' && !items.length) message = t('stream.boost.desk.animation.loading');
  else if (status === 'error') message = t('stream.boost.desk.animation.loadError');
  else if (busy) message = t('stream.boost.desk.animation.saving');
  else if (error) message = t(`stream.boost.desk.animation.error.${boostAnimationError(error)}`);
  else if (saved) message = t('stream.boost.desk.animation.saved', { name: saved });

  return (
    <div className="boost-anim" data-testid="boost-animation">
      <div className="boost-anim-head">
        <div className="boost-anim-current">
          <span className="boost-desk-heading">{t('stream.boost.desk.animation.heading')}</span>
          <span className="boost-anim-name" data-testid="boost-animation-equipped">{equipped?.name ?? '–'}</span>
        </div>
        {selected && (
          <button
            type="button"
            className="boost-anim-toggle"
            onClick={() => setPreviewing((on) => !on)}
            aria-expanded={previewing}
            data-testid="boost-animation-preview-toggle"
          >
            {previewing ? t('stream.boost.desk.animation.hidePreview') : t('stream.boost.desk.animation.preview')}
          </button>
        )}
      </div>

      {items.length > 1 && (
        <div className="boost-anim-list" role="radiogroup" aria-label={t('stream.boost.desk.animation.listLabel')}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="radio"
              aria-checked={item.id === selected?.id}
              className={`boost-anim-chip${item.id === selected?.id ? ' boost-anim-chip--selected' : ''}`}
              onClick={() => choose(item)}
              disabled={busy}
              data-testid="boost-animation-chip"
            >
              {item.name}
              {item.id === equippedId && (
                <span className="boost-anim-worn">{t('stream.boost.desk.animation.worn')}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {selected && selected.id !== equippedId && (
        <button
          type="button"
          className="boost-anim-use"
          onClick={use}
          disabled={busy}
          aria-busy={busy || undefined}
          data-testid="boost-animation-use"
        >
          {t('stream.boost.desk.animation.use', { name: selected.name })}
        </button>
      )}

      {previewing && selected && (
        <FxPreview key={`${selected.id}:${selected.fxPackageId}`} item={previewItem(selected)} timing={previewTiming} />
      )}

      {message && (
        <p className="boost-anim-status" data-testid="boost-animation-status" aria-live="polite">
          {message}
          {status === 'error' && (
            <button type="button" className="boost-anim-retry" onClick={anims.retry}>
              {t('stream.boost.desk.animation.retry')}
            </button>
          )}
        </p>
      )}
    </div>
  );
};

export default BoostAnimationPicker;
